import { Toast } from '@components/Toast';
import { faListTimeline, faTrash } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useDeleteStep } from '@partful/features/instructions/steps/lib/useDeleteStep';
import {
  SegmentModel,
  SegmentWithContentModel,
  StepModel,
  StepWithContentModel,
  VersionModel,
} from '@partful/instructions-domain-models';
import { Button } from '@partful/shared/ui/Button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogPanel } from '@partful/shared/ui/Dialog/Dialog';
import { Typography } from '@partful/shared/ui/Typography';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './StepList.module.scss';

export type StepListProps = {
  steps: (StepModel & Partial<StepWithContentModel>)[];
  isEditing: boolean;
  segment: SegmentModel & Partial<SegmentWithContentModel>;
  version: VersionModel;
};

export function StepList({ steps, isEditing, segment, version }: Readonly<StepListProps>) {
  const { t: i18n } = useTranslation();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [stepToDelete, setStepToDelete] = useState<StepModel & Partial<StepWithContentModel>>();

  const {
    mutateAsync: deleteStep,
    isSuccess: isSuccessDeletingStep,
    isError: isErrorDeletingStep,
    isLoading: isDeletingStep,
  } = useDeleteStep();

  const handleDeleteStep = async (stepId: string) => {
    try {
      await deleteStep({
        versionId: version.id,
        instructionId: version.instructionId,
        segmentId: segment.id,
        stepId,
      });
    } catch (e) {
      // Don't need to handle this here
    }
  };

  if (steps.length === 0) {
    return (
      <div className={styles.noSteps}>
        <FontAwesomeIcon icon={faListTimeline} />
        <Typography whiteSpace="pre-line">{i18n('instructions:manage.version.segments.noSteps')}</Typography>
      </div>
    );
  }

  return (
    <>
      <ol className={styles.stepList}>
        {steps.map((step, index) => (
          <li data-testid={`step-item-${index}`} key={step.id} className={styles.step}>
            <div className={styles.index}>{index + 1}</div>
            <div className={styles.details}>
              <Typography appearance="label" fontWeight={600} overflow="hidden" textOverflow="ellipsis">
                {step.content?.name || i18n('instructions:stepBuilder.stepTitle')}
              </Typography>
              <Typography appearance="label" overflow="hidden" textOverflow="ellipsis">
                {step.content?.description}
              </Typography>
            </div>
            {isEditing && (
              <div className={styles.actions}>
                <Button
                  name="instructions_version_langandinstructions_delete_step_click"
                  data-testid={`delete-step-${index}`}
                  disabled={!isEditing}
                  variant="destructive"
                  size="small"
                  onClick={() => {
                    setConfirmDelete(true);
                    setStepToDelete(step);
                  }}
                >
                  <FontAwesomeIcon icon={faTrash} />
                  {i18n('instructions:step.actions.delete')}
                </Button>
              </div>
            )}
          </li>
        ))}
      </ol>
      {stepToDelete && (
        <Dialog
          open={confirmDelete}
          modal
          onClose={/* istanbul ignore next - not testing native <dialog> behaviour */ () => setConfirmDelete(false)}
        >
          <DialogPanel>
            <DialogHeader>
              <Typography as="p" appearance="heading5" overflow="hidden" textOverflow="ellipsis">
                {i18n('instructions:step.actions.deleteConfirm.title', {
                  stepName: /* istanbul ignore next  */ stepToDelete.content?.name
                    ? `'${stepToDelete.content.name}'`
                    : steps.indexOf(stepToDelete) + 1,
                })}
              </Typography>
            </DialogHeader>
            <DialogContent>{i18n('instructions:step.actions.deleteConfirm.message')}</DialogContent>
            <DialogFooter>
              <Button
                name="instructions_version_langandinstructions_delete_step_confirm_click"
                data-testid={`confirm-delete-step-${steps.indexOf(stepToDelete)}`}
                variant="destructive"
                onClick={async () => {
                  await handleDeleteStep(stepToDelete.id);
                  setConfirmDelete(false);
                }}
                disabled={isDeletingStep}
              >
                {i18n('instructions:common.delete')}
              </Button>
              <Button
                name="instructions_version_langandinstructions_delete_step_cancel_click"
                variant="tertiary"
                onClick={/* istanbul ignore next */ () => setConfirmDelete(false)}
                disabled={isDeletingStep}
              >
                {i18n('instructions:common.cancel')}
              </Button>
            </DialogFooter>
          </DialogPanel>
        </Dialog>
      )}
      <Toast
        dependency={isErrorDeletingStep}
        severity="error"
        title={i18n('instructions:step.actions.deleteError.title.title')}
        message={i18n('instructions:step.actions.deleteError.title.message')}
        testId="delete-step-error-message"
      />
      <Toast
        dependency={isSuccessDeletingStep}
        severity="success"
        title={i18n('instructions:step.actions.deleteSuccess.title')}
        testId="delete-step-success-message"
      />
    </>
  );
}
